import React, { useState } from "react";
import { crd, rad } from "../../constants/styles";
import { downloadCsv, downloadPdf } from "../../utils/exportTable";

// Reusable transactions list with:
//   - Cards / List view toggle
//   - Date range filter (Last month / 6m / 12m / Custom)
//   - Sortable column headers (List view)
//   - CSV / PDF download (exports the *visible* sorted+filtered set so
//     the file matches what's on screen)
//
// Used by TenPayHist (tenant), TenantPayHistoryPage (landlord), and
// SpEarnings (service person). Each caller supplies its own column
// config and card renderer, so the table fits whatever data shape it
// has without forcing a one-size-fits-all schema.
//
// Props
//   rows             array of row objects
//   columns          [{ key, label, type, align, sortable, valueOf, format }]
//                      type: "date" | "number" | "text" (controls sort + export)
//                      valueOf(row): override how a sort/filter value is computed
//                      format(row): how the cell is rendered in list view
//   renderCard       (row, idx) => JSX for cards view (optional; falls back to a
//                      simple list of label/value pairs from columns)
//   dateField        column key used for the range filter (default "dt")
//   h, t             theme + translations from context
//   title            PDF title
//   exportFilename   base filename (date stamp appended)
//   showViewToggle   default true
//   showRangeFilter  default true
//   defaultRange     "1m" | "6m" | "12m" | "all" — initial range pill
const TxnTable = ({
  rows,
  columns,
  renderCard,
  dateField = "dt",
  h,
  t,
  title = "Transactions",
  exportFilename = "transactions",
  showViewToggle = true,
  showRangeFilter = true,
  defaultRange = "6m",
  defaultView = "cards",
  defaultSortKey,
  defaultSortDir = "desc",
}) => {
  const initialSortKey = defaultSortKey || dateField;
  const [view, setView] = useState(defaultView);
  const [range, setRange] = useState(defaultRange);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDir, setSortDir] = useState(defaultSortDir);

  // ── Helpers ───────────────────────────────────────────────────
  const parseDt = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };
  const now = new Date();
  const monthsAgo = (n) => { const x = new Date(now); x.setMonth(x.getMonth() - n); x.setHours(0,0,0,0); return x; };
  const startOf = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };

  // ── Filter by date range ──────────────────────────────────────
  const inRange = (row) => {
    const d = parseDt(row[dateField]);
    // Keep rows with bad/missing dates visible — losing a payment to
    // a date parsing quirk is worse than showing one extra row.
    if (!d) return true;
    if (range === "1m") return d >= monthsAgo(1);
    if (range === "6m") return d >= monthsAgo(6);
    if (range === "12m") return d >= monthsAgo(12);
    if (range === "custom") {
      const from = customFrom ? startOf(customFrom) : null;
      const to = customTo ? startOf(customTo) : null;
      if (from && d < from) return false;
      if (to) { const toEnd = new Date(to); toEnd.setHours(23,59,59,999); if (d > toEnd) return false; }
      return true;
    }
    return true; // "all"
  };
  const visibleRows = (rows || []).filter(inRange);

  // ── Sort comparator ───────────────────────────────────────────
  const colByKey = (k) => columns.find(c => c.key === k);
  const valueOf = (row, key) => {
    const col = colByKey(key);
    if (col && typeof col.valueOf === "function") return col.valueOf(row);
    const v = row[key];
    if (col && col.type === "date") {
      const d = parseDt(v);
      return d ? d.getTime() : -Infinity;
    }
    if (col && col.type === "number") return Number(v) || 0;
    return (v == null ? "" : String(v)).toLowerCase();
  };
  const sortedRows = visibleRows.slice().sort((a, b) => {
    const va = valueOf(a, sortKey), vb = valueOf(b, sortKey);
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // ── Sort click handler ────────────────────────────────────────
  const onSort = (k) => {
    const col = colByKey(k);
    if (!col || col.sortable === false) return;
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      // Numeric/date columns default to desc (newest/largest first);
      // text columns default to asc (A→Z).
      setSortDir(col.type === "date" || col.type === "number" ? "desc" : "asc");
    }
  };
  const arrow = (k) => sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅";

  // ── Export ────────────────────────────────────────────────────
  const exportRows = sortedRows.map(row => {
    const out = {};
    columns.forEach(c => {
      // For export, prefer raw value (CSV-friendly). If a column has a
      // `format` function we still pass the underlying field rather
      // than rendered JSX.
      out[c.key] = row[c.key] == null ? "" : row[c.key];
    });
    return out;
  });
  const exportHeaders = columns.map(c => ({ key: c.key, label: c.label }));
  const today = new Date().toISOString().slice(0, 10);
  const rangeLabel = range === "1m" ? "Last 1 month"
    : range === "6m" ? "Last 6 months"
    : range === "12m" ? "Last 12 months"
    : range === "custom" ? `${customFrom || "—"} to ${customTo || "—"}`
    : "All time";

  // ── Styles ────────────────────────────────────────────────────
  const pills = [
    ["1m", "Last month"],
    ["6m", "Last 6 months"],
    ["12m", "Last 12 months"],
    ["custom", "Custom"],
  ];
  const dateInputStyle = { padding: "4px 8px", borderRadius: 8, border: `1px solid ${h.bd || h.sc + "30"}`, background: h.cd, color: h.tx, fontSize: 12, colorScheme: h.dk ? "dark" : "light" };
  const exportBtnStyle = { padding: "6px 12px", borderRadius: rad.sm, border: `1px solid ${h.ac}`, background: "transparent", color: h.ac, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 };
  const thBase = { padding: "8px 8px", color: h.ts, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", borderBottom: `1px solid ${h.bd || h.sc + "20"}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: h.cd, userSelect: "none" };
  const td = { padding: "8px 8px", fontSize: 12, color: h.tx, borderBottom: `1px solid ${h.bd || h.sc + "15"}`, whiteSpace: "nowrap" };

  // Empty rows — render filter UI so user can widen, but skip table.
  const isEmpty = !rows || rows.length === 0;

  return <>
    {/* View toggle + range filter row */}
    {(showViewToggle || showRangeFilter) && <>
      {showViewToggle && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <div style={{ display: "inline-flex", background: h.ib, borderRadius: rad.sm + 2, padding: 2 }}>
            {[["cards", "▦", "Cards"], ["list", "≡", "List"]].map(([id, icon, label]) => {
              const active = view === id;
              return <button
                key={id}
                onClick={() => setView(id)}
                aria-label={`${label} view`}
                aria-pressed={active}
                title={`${label} view`}
                style={{
                  padding: "4px 10px",
                  borderRadius: rad.sm,
                  border: "none",
                  background: active ? h.cd : "transparent",
                  color: active ? h.ac : h.ts,
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  boxShadow: active ? h.sh : "none",
                }}>
                <span aria-hidden="true" style={{ fontSize: 13 }}>{icon}</span>{label}
              </button>;
            })}
          </div>
        </div>
      )}

      {showRangeFilter && (
        <div style={{ display: "flex", gap: 6, marginBottom: 10, overflowX: "auto", paddingBottom: 2, WebkitOverflowScrolling: "touch" }}>
          {pills.map(([id, label]) => {
            const active = range === id;
            return <button
              key={id}
              onClick={() => setRange(id)}
              style={{
                flexShrink: 0,
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? h.ac : h.bd || h.sc + "30"}`,
                background: active ? h.ac : "transparent",
                color: active ? "#fff" : h.tx,
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}>{label}</button>;
          })}
        </div>
      )}

      {showRangeFilter && range === "custom" && (
        <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ color: h.sc, fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
            From <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={dateInputStyle} />
          </label>
          <label style={{ color: h.sc, fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
            To <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={dateInputStyle} />
          </label>
        </div>
      )}
    </>}

    {/* Count + download buttons */}
    {!isEmpty && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ color: h.sc, fontSize: 11 }}>
          Showing {sortedRows.length} of {rows.length} {rows.length === 1 ? "entry" : "entries"}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => downloadCsv(exportRows, exportHeaders, `${exportFilename}-${today}.csv`)} title="Download CSV" aria-label="Download CSV" style={exportBtnStyle}>
            <span aria-hidden="true">⤓</span> CSV
          </button>
          <button onClick={() => downloadPdf(exportRows, exportHeaders, title, rangeLabel)} title="Download PDF" aria-label="Download PDF" style={{ ...exportBtnStyle, background: h.ac, color: "#fff" }}>
            <span aria-hidden="true">⤓</span> PDF
          </button>
        </div>
      </div>
    )}

    {/* Empty states */}
    {isEmpty && (
      <p style={{ color: h.sc, textAlign: "center", padding: 24 }}>{(t && t.noData) || "No data"}</p>
    )}
    {!isEmpty && sortedRows.length === 0 && (
      <p style={{ color: h.sc, textAlign: "center", padding: 24, fontSize: 13 }}>
        No entries in this range. Try a wider window.
      </p>
    )}

    {/* Cards view */}
    {view === "cards" && sortedRows.map((row, i) =>
      renderCard ? renderCard(row, i) : <div key={row.id || i} style={crd(h)}>
        {columns.map(c => <div key={c.key} style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>
          {c.label}: <span style={{ color: h.tx, fontWeight: 500 }}>{c.format ? c.format(row) : (row[c.key] ?? "—")}</span>
        </div>)}
      </div>
    )}

    {/* List view */}
    {view === "list" && sortedRows.length > 0 && (
      <div style={{ ...crd(h), padding: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              {columns.map(c => {
                const active = sortKey === c.key;
                const sortable = c.sortable !== false;
                return <th
                  key={c.key}
                  onClick={sortable ? () => onSort(c.key) : undefined}
                  title={sortable ? `Sort by ${c.label}` : c.label}
                  aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  style={{ ...thBase, textAlign: c.align || "left", color: active ? h.ac : h.ts, cursor: sortable ? "pointer" : "default" }}>
                  {c.label}{sortable && <span style={{ fontSize: 9, opacity: active ? 1 : 0.5 }}>{arrow(c.key)}</span>}
                </th>;
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => (
              <tr key={row.id || i} style={{ background: i % 2 === 0 ? "transparent" : (h.ib || `${h.sc}08`) }}>
                {columns.map(c => (
                  <td key={c.key} style={{ ...td, textAlign: c.align || "left" }}>
                    {c.format ? c.format(row) : (row[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>;
};

export default TxnTable;
